import type { RouteLocationRaw } from 'vue-router';
import type { ApolloClient } from '@apollo/client';
import { isGranted } from '@pcg/auth';
import { HomeViewKind, ListFavoritesDocument } from '@teamango/graphql';
import type { Permission } from '@teamango/permissions';

import { favoritePath, type ResolvedFavorite } from '@/shared/composables/useFavorites';
import type { CurrentUser } from '@/shared/types/auth';

/** Where `/` goes without a setting — what it always did before #581. */
export const DEFAULT_HOME_ROUTE: RouteLocationRaw = { name: 'Issues' };

/** The default pages a home view can name, and the permission each route authorizes with. */
export const HOME_PAGES: { kind: HomeViewKind; label: string; icon: string; route: string; permission: Permission }[] = [
  { kind: HomeViewKind.INBOX, label: 'Inbox', icon: 'i-lucide-inbox', route: 'Inbox', permission: 'tm:core:notifications:list' },
  { kind: HomeViewKind.ISSUES, label: 'Issues', icon: 'i-lucide-square-kanban', route: 'Issues', permission: 'tm:core:issues:list' },
];

/** A favorite is usable as home only while it still resolves to a link. */
export const isResolvedFavorite = (
  favorite: { savedView?: { scopeKey?: string | null } | null },
): favorite is ResolvedFavorite => Boolean(favorite.savedView?.scopeKey);

/**
 * Where `/` sends this user (#581). Every way the setting can have gone stale
 * — the view unfavorited, deleted, made personal by its owner, or left the
 * user's perimeter — ends in the default, never in an error: `favorites` is
 * already filtered server-side to the views the caller can open, so "still in
 * that list" is the whole check. The list is fetched network-only because the
 * favorite may have been removed on another device since this tab loaded.
 */
export const resolveHomeRoute = async (
  user: CurrentUser | null | undefined,
  client: ApolloClient,
): Promise<RouteLocationRaw> => {
  const homeView = user?.homeView;

  if (!user || !homeView) {
    return DEFAULT_HOME_ROUTE;
  }

  if (homeView.kind === HomeViewKind.SAVED_VIEW) {
    if (!homeView.savedViewId || !isGranted(user, 'tm:core:favorites:list')) {
      return DEFAULT_HOME_ROUTE;
    }

    try {
      const result = await client.query({ query: ListFavoritesDocument, fetchPolicy: 'network-only' });
      const favorite = (result.data?.favorites ?? [])
        .filter(isResolvedFavorite)
        .find((candidate) => candidate.savedViewId === homeView.savedViewId);
      const path = favorite ? favoritePath(favorite) : null;

      return path ?? DEFAULT_HOME_ROUTE;
    } catch (error) {
      console.error('[home-view] Failed to resolve the home favorite:', error);

      return DEFAULT_HOME_ROUTE;
    }
  }

  const page = HOME_PAGES.find((candidate) => candidate.kind === homeView.kind);

  return page && isGranted(user, page.permission) ? { name: page.route } : DEFAULT_HOME_ROUTE;
};
